import React, { Component } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { marked } from "marked";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import DetailsExplanation from "./DetailsExplanation";

/**
 * MathType/Wiris write an accent as <mover>/<munder> whose script is the accent
 * glyph itself: u-hat is <mover><mi>u</mi><mo>^</mo></mover>. Joining those
 * children produces "u^" - a script operator with nothing after it - and
 * MathJax prints "Missing superscript or subscript argument" in place of the
 * formula. Accents therefore map to real LaTeX macros instead.
 */
const ACCENT_MACROS = {
  "^": "\\hat",
  "ˆ": "\\hat",
  "̂": "\\hat",
  "~": "\\tilde",
  "˜": "\\tilde",
  "̃": "\\tilde",
  "→": "\\vec",
  "⃗": "\\vec",
  "←": "\\overleftarrow",
  "¯": "\\bar",
  "‾": "\\bar",
  "̄": "\\bar",
  "˙": "\\dot",
  "̇": "\\dot",
  "¨": "\\ddot",
  "̈": "\\ddot",
  "ˇ": "\\check",
  "˘": "\\breve",
  "´": "\\acute",
  "`": "\\grave",
  "_": "\\underline",
};

// Last line of defence: a script operator with no argument after it is the one
// thing that makes MathJax replace a formula with an error box.
const dropDanglingScripts = (s) =>
  String(s).replace(/[\^_](?!\s*(?:\{|\\[a-zA-Z]|[A-Za-z0-9]))/g, "");

class AiSimilarQuestionModels2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalShow: false,
      similar: [],
      loading: false,
      explanationLoading: false,
      detailedLoading: false,
      error: null,
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
      dataClear: 0,

      // How many questions the last request asked for. Generate raises it by 3.
      count: 3,

      // Tabs: "similar" (default) | "details". detailsRequested stays false
      // until the user first opens the Details tab, so its API is called lazily.
      activeTab: "similar",
      detailsRequested: false,
    };
  }

  selectTab = (tab) => {
    this.setState((prev) => ({
      activeTab: tab,
      detailsRequested: prev.detailsRequested || tab === "details",
    }));
  };


  componentDidUpdate() {
    if (this.props.show === true && this.state.dataClear == 0) {
      this.setState({
        modalShow: false,
        similar: [],
        loading: false,
        explanationLoading: false,
        detailedLoading: false,
        error: null,
        currentIndex: 0,
        showExplanation: false,
        showDetailedExplanation: false,
        selectedOption: null,
        answered: false, dataClear: 1
      })
    }
    if (this.props.show === false && this.state.dataClear == 1) {
      this.setState({ dataClear: 0 })
    }
  };


  // Helpers reused across methods to avoid double-encoding and to produce
  // application/x-www-form-urlencoded style encoding (spaces -> +).
  safeDecode = (s) => {
    if (!s || typeof s !== "string") return s;
    let out = s;
    try {
      for (let i = 0; i < 3; i++) {
        const dec = decodeURIComponent(out);
        if (dec === out) break;
        out = dec;
      }
    } catch (e) {
      return s;
    }
    return out;
  };

  encodeForm = (s) => {
    try {
      if (s === null || typeof s === "undefined") return "";
      const dec = this.safeDecode(String(s));
      return encodeURIComponent(dec).replace(/%20/g, "+");
    } catch (e) {
      return encodeURIComponent(String(s)).replace(/%20/g, "+");
    }
  };

  handleSelectOption = (key) => {
    if (this.state.answered) return;
    this.setState({ selectedOption: key, answered: true });
  };

  // ✅ cache key by question_id
  getCacheKey = () => {
    const questionId = this.props.question_id || "no_id";
    return `ai_similar_questions_${questionId}`;
  };

  open = () => this.setState({ modalShow: true });
  close = () =>
    this.setState({
      modalShow: false,
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
    });

  loadFromCache = () => {
    try {
      const cached = localStorage.getItem(this.getCacheKey());
      if (!cached) return null;
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  };

  saveToCache = (data) => {
    try {
      localStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  };

  // ✅ ONLY TEXT should be sent to API
  cleanQuestionForApi = (input) => {
    if (!input) return "";

    let text = String(input);

    // ✅ 1) Remove HTML tags completely
    text = text.replace(/<\/?[^>]+(>|$)/g, " ");

    // ✅ 2) Convert common HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // ✅ 3) Remove MathML tags if present (send only text)
    text = text.replace(/<math[\s\S]*?<\/math>/g, " ");

    // ✅ 4) Normalize extra spaces
    text = text.replace(/\s+/g, " ").trim();

    return text;
  };

  // Normalize option/question/explanation text for rendering math correctly
  normalizeMathText = (input) => {
    if (!input) return "";
    let text = String(input);

    // Convert any inline MathML to LaTeX (best-effort) before further processing.
    // Namespace prefixes are allowed (<m:math>, <mml:math>) - matching only
    // "<math" would leave those for the tag stripper to delete.
    const mathmlMatches = [];
    text = text.replace(/<([a-z][\w-]*:)?math\b[\s\S]*?<\/([a-z][\w-]*:)?math>/gi, (m) => {
      mathmlMatches.push(m);
      return `@@MATHML_REPLACE_${mathmlMatches.length - 1}@@`;
    });

    const mathMLToLatex = (mathString) => {
      try {
        const parser = new DOMParser();
        let doc = parser.parseFromString(mathString, "application/xml");

        // Malformed MathML (unclosed <mrow>, stray tags, ...) makes the XML
        // parser return a <parsererror> document instead of throwing. Its text
        // is the browser's own message ("This page contains the following
        // errors: ...") which would otherwise be converted and rendered as if
        // it were the question. Re-parse leniently as HTML in that case.
        if (doc.getElementsByTagName("parsererror").length) {
          doc = parser.parseFromString(mathString, "text/html");
        }

        // Structural elements (msup/msub/mfrac) index children positionally, so
        // whitespace-only text nodes between tags must not be counted.
        const kids = (node) =>
          [...node.childNodes].filter(
            (n) => !(n.nodeType === 3 && !(n.nodeValue || "").trim())
          );

        const convert = (node) => {
          if (!node) return "";
          if (node.nodeType === 3) return node.nodeValue || "";
          // Namespaced markup (<m:math>, <mml:mi>) is common and must resolve to
          // the same element names.
          const name = node.nodeName.toLowerCase().replace(/^[a-z][\w-]*:/, "");

          // A parser-error subtree carries the browser's message, not math.
          if (name === "parsererror") return "";

          // MathType keeps its own source encoding in <annotation>; it is not
          // meant to be displayed and would otherwise be concatenated onto the
          // formula as gibberish (MathType@MTEF@5@5@+=feaagKart...).
          if (name === "annotation" || name === "annotation-xml") return "";

          // Accents (u-hat, A-vector) live here, and joining their children
          // would emit a bare "^" that MathJax refuses to typeset.
          if (name === "mover" || name === "munder") {
            const c = kids(node);
            const base = c[0] ? convert(c[0]) : "";
            const script = (c[1] ? convert(c[1]) : "").trim();
            const accent = ACCENT_MACROS[script];
            if (accent) return `${accent}{${base}}`;
            const safe = script.replace(/[\^_]/g, "");
            return name === "mover"
              ? `\\overset{${safe}}{${base}}`
              : `\\underset{${safe}}{${base}}`;
          }
          if (name === "msubsup" || name === "munderover") {
            const c = kids(node);
            const a = c[0] ? convert(c[0]) : "";
            const b = c[1] ? convert(c[1]) : "";
            const d = c[2] ? convert(c[2]) : "";
            return `${a}_{${b}}^{${d}}`;
          }
          if (name === "mroot") {
            const c = kids(node);
            const a = c[0] ? convert(c[0]) : "";
            const b = c[1] ? convert(c[1]) : "";
            return `\\sqrt[${b}]{${a}}`;
          }

          if (name === "msup") {
            const c = kids(node);
            const a = c[0] ? convert(c[0]) : "";
            const b = c[1] ? convert(c[1]) : "";
            return `${a}^{${b}}`;
          }
          if (name === "msub") {
            const c = kids(node);
            const a = c[0] ? convert(c[0]) : "";
            const b = c[1] ? convert(c[1]) : "";
            return `${a}_{${b}}`;
          }
          if (name === "mfrac") {
            const c = kids(node);
            const a = c[0] ? convert(c[0]) : "";
            const b = c[1] ? convert(c[1]) : "";
            return `\\frac{${a}}{${b}}`;
          }
          if (name === "msqrt") {
            const inner = [...node.childNodes].map(convert).join("");
            return `\\sqrt{${inner}}`;
          }
          if (name === "mfenced") {
            const inner = [...node.childNodes].map(convert).join("");
            return `(${inner})`;
          }
          if (name === "mrow" || name === "semantics" || name === "mstyle" || name === "mpadded") {
            return [...node.childNodes].map(convert).join("");
          }
          if (name === "mi" || name === "mn" || name === "mo" || name === "mtext") {
            // A lone "^" or "_" here is an accent glyph that <mover> did not
            // pick up, never a script operator; passing it on breaks the formula.
            return (node.textContent || "").replace(/[\^_]/g, "");
          }
          // fallback: join children
          return [...node.childNodes].map(convert).join("");
        };

        // After an HTML re-parse the root is <html>, so target the <math> node.
        const root = doc.querySelector("math") || doc.documentElement || doc.firstChild;
        const latex = convert(root);

        // Last-resort guard so a parser message can never reach the renderer.
        if (/This page contains the following errors|error on line \d+ at column/i.test(latex)) {
          return "";
        }

        return dropDanglingScripts(latex) || "";
      } catch (e) {
        return "";
      }
    };

    // restore converted MathML as LaTeX wrapped in $...$
    if (mathmlMatches.length) {
      mathmlMatches.forEach((m, idx) => {
        const latex = mathMLToLatex(m);
        // When conversion yields nothing (unrecoverable markup), keep the plain
        // text of the expression rather than dropping it from the question.
        const replacement = latex
          ? `$${latex}$`
          : m.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
        // Function form: a literal replacement could be mangled by $& / $1 patterns.
        text = text.replace(`@@MATHML_REPLACE_${idx}@@`, () => replacement);
      });
    }

    // Unescape common HTML entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Preserve LaTeX delimiters: convert escaped \( \) to $...$ and \[ \] to $$..$$
    text = text.replace(/\\\(/g, "$");
    text = text.replace(/\\\)/g, "$");
    // A literal "$$" in a replacement string means one "$" to String.replace, so
    // display delimiters have to be written as a function to survive.
    text = text.replace(/\\\[/g, () => "$$");
    text = text.replace(/\\\]/g, () => "$$");

    // The tag stripper below cannot tell "<" in a formula ("a < b") from the
    // start of a tag and would delete everything up to the next ">", taking half
    // the sentence and the tail of a formula with it. Formulas are parked first.
    const formulas = [];
    text = text.replace(/\$\$[\s\S]+?\$\$|\$[^$\n]+?\$/g, (m) => {
      formulas.push(m);
      return `@@MJXKEEP_${formulas.length - 1}@@`;
    });

    // Remove any remaining HTML tags but keep content
    text = text.replace(/<[^>]+>/g, " ");

    if (formulas.length) {
      formulas.forEach((m, idx) => {
        text = text.replace(`@@MJXKEEP_${idx}@@`, () => m);
      });
    }

    // Normalize whitespace but preserve paragraph breaks/newlines so markdown
    // structures (headings, lists, horizontal rules) survive.
    // - Normalize CRLF to LF
    // - Collapse 3+ newlines into 2
    // - Collapse multiple spaces/tabs within a line
    text = text.replace(/\r\n/g, "\n");
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text
      .split("\n")
      .map((line) => line.replace(/[ \t]+/g, " ").trim())
      .join("\n");
    // Ensure at most one blank line between paragraphs and trim edges
    text = text.replace(/\n\s*\n+/g, "\n\n").trim();

    return text;
  };

  // `requestedCount` is passed explicitly rather than read from state so the
  // caller is not racing setState: Generate needs the new value immediately.
  fetchSimilarFromApi = async (requestedCount) => {
    const count = Math.max(3, parseInt(requestedCount, 10) || this.state.count || 3);

    const rawQuery = this.props.question || this.props.triggerQueryText || "sample question";
    const decodedRaw = this.safeDecode(rawQuery);

    // ✅ Prepare question for API: convert MathML to LaTeX and preserve markdown
    // Use normalizeMathText which performs MathML->LaTeX conversion and preserves paragraph breaks.
    let query = this.normalizeMathText(decodedRaw);
    if (!query) query = this.cleanQuestionForApi(decodedRaw);

    // Use props when provided (safe-decode to avoid double-encoding issues)
    const studentClass = this.safeDecode(this.props.student_class || this.props.className || "");
    const subject = this.safeDecode(this.props.subject || "");
    const chapter = this.safeDecode(this.props.chapter || "");
    const exam = this.safeDecode("JEE Main");

    this.setState({
      loading: true,
      error: null,
      similar: [],
      currentIndex: 0,
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
      count,
    });

    this.open();

    try {
      // encode as application/x-www-form-urlencoded style (spaces -> +)
      const qParam = this.encodeForm(query);
      const scParam = this.encodeForm(studentClass);
      const subjParam = this.encodeForm(subject);
      const chapParam = this.encodeForm(chapter);
      const examParam = this.encodeForm(exam);

      // Use GET endpoint to fetch similar questions
      const url = `https://ai.rizee.in/get-similar-questions-3?question=${qParam}&student_class=${scParam}&subject=${subjParam}&exam=${examParam}&count=${count}&difficulty=Hard`

      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API error ${res.status}: ${text}`);
      }

      const rawText = await res.text();
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        data = rawText;
      }

      // Try multiple possible shapes in response
      let questions = [];
      if (Array.isArray(data?.questions)) questions = data.questions;
      else if (Array.isArray(data)) questions = data;
      else if (Array.isArray(data?.results)) questions = data.results;
      else if (Array.isArray(data?.data)) questions = data.data;
      else if (typeof data === "string" && data.trim().length > 0) {
        // Parse plain-text MCQ response into structured objects
        const normalize = (s) => this.normalizeMathText(s);

        const parsePlain = (text) => {
          const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
          const out = [];
          let cur = null;
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const qMatch = line.match(/^Q\s*\d+\.?\s*(.*)/i);
            // accept formats: A.  A)  A -  A:  A  (with space)
            const optMatch = line.match(/^([A-D])(?:[\.\)\-:\s]+)(.+)$/i);
            // Accept "Correct Answer: C", "Correct Answer - C", "Answer: Option C", or text answers
            const correctLetterMatch = line.match(/^(?:Correct\s*Answer|Answer)[:\-\s]*([A-D])\b/i);
            const correctTextMatch = line.match(/^(?:Correct\s*Answer|Answer)[:\-\s]*(.+)$/i);

            if (qMatch) {
              if (cur) out.push(cur);
              cur = { question: normalize(qMatch[1].trim()), options: {}, correct_answer: null, explanation: "" };
              continue;
            }

            if (optMatch && cur) {
              const key = optMatch[1].toUpperCase();
              cur.options[key] = normalize(optMatch[2].trim());
              // If we previously had a correct letter, ensure it's applied
              if (cur._pendingCorrectLetter) {
                cur.correct_answer = cur._pendingCorrectLetter;
                delete cur._pendingCorrectLetter;
              }
              continue;
            }

            if (correctLetterMatch && cur) {
              // If options already parsed, set directly; otherwise remember pending
              const letter = correctLetterMatch[1].toUpperCase();
              if (Object.keys(cur.options).length) cur.correct_answer = letter;
              else cur._pendingCorrectLetter = letter;
              continue;
            }

            if (correctTextMatch && cur) {
              const ansText = correctTextMatch[1].trim();
              const normAns = normalize(ansText).toLowerCase();
              // Try to match the answer text to one of the collected options
              let matched = false;
              for (const k of ["A", "B", "C", "D"]) {
                if (cur.options[k] && cur.options[k].toLowerCase().includes(normAns)) {
                  cur.correct_answer = k;
                  matched = true;
                  break;
                }
              }
              // If it looks like a single letter there, handle it
              const singleLetter = ansText.match(/^([A-D])$/i);
              if (!matched && singleLetter) {
                const letter = singleLetter[1].toUpperCase();
                if (Object.keys(cur.options).length) cur.correct_answer = letter;
                else cur._pendingCorrectLetter = letter;
                matched = true;
              }
              if (matched) continue;
            }

            // fallback: append to last question text if it looks like continuation
            if (cur && cur.question && !Object.keys(cur.options).length) {
              cur.question = normalize(cur.question + ' ' + line);
            } else if (cur && Object.keys(cur.options).length && !cur.correct_answer) {
              // treat as explanation lines
              cur.explanation = normalize((cur.explanation + ' ' + line).trim());
            }
          }
          if (cur) out.push(cur);
          return out;
        };

        questions = parsePlain(data);
      }

      // save cache when question_id present
      if (this.props.question_id) {
        this.saveToCache({
          storedAt: Date.now(),
          question_id: this.props.question_id,
          questions,
        });
      }

      this.setState({ similar: questions, loading: false, selectedOption: null, answered: false });
    } catch (err) {
      this.setState({
        error: err.message || String(err),
        loading: false,
      });
    }
  };

  // Opening for a question always starts on the Similar tab with the count back
  // at 3; whatever the previous question was generated up to does not carry over.
  openSimilarQuestions = () => {
    this.setState({ activeTab: "similar", detailsRequested: false });

    if (!this.props.question_id) {
      this.fetchSimilarFromApi(3);
      return;
    }

    const cached = this.loadFromCache();

    if (cached?.questions && Array.isArray(cached.questions) && cached.questions.length > 0) {
      this.setState({
        modalShow: true,
        similar: cached.questions,
        loading: false,
        error: null,
        currentIndex: 0,
        showExplanation: false,
        selectedOption: null,
        answered: false,
        // The cache already holds everything that was generated last time, so
        // Generate carries on from there instead of asking for fewer again.
        count: Math.max(3, cached.questions.length),
      });
    } else {
      this.fetchSimilarFromApi(3);
    }
  };

  // Each click asks for three more than the last request and shows whatever
  // the API returns.
  generateMore = () => {
    if (this.state.loading) return;
    this.fetchSimilarFromApi(this.state.count + 3);
  };

  componentDidUpdate(prevProps) {
    if (this.props.triggerQuery !== prevProps.triggerQuery) {
      this.openSimilarQuestions();
    }
  }

  // ✅ UI display using MathJax (render question/explanation nicely)
  renderMathContent = (text, variant = "normal") => {
    let md = text || "";

    // For explanation variant, ensure a leading blank line when the content
    // starts with a markdown heading or list so it doesn't stick to the box edge.
    if (variant === "small") {
      // If content starts with a heading, list, or numbered list, add a leading newline
      const startsWithBlock = /^\s*(#{1,6}\s|[-*+]\s|\d+\.\s)/.test(md);
      // Also handle cases where the AI returns a horizontal rule immediately followed
      // by a heading without a newline, e.g. "--- ####". Detect HR followed by optional
      // whitespace and a heading/list marker and treat as block-start.
      const hrFollowedByBlock = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*(?:#{1,6}\s|[-*+]\s|\d+\.\s)/.test(md);
      if (startsWithBlock || hrFollowedByBlock) md = "\n" + md;
    }

    // Let marked produce semantic HTML (keep block structure); don't strip newlines.
    const html = marked.parse(md || "");
    const style = variant === "small" ? styles.explanationText : styles.answerContent;
    return (
      <MathJax dynamic>
        <div
          className={variant === "small" ? "ai-sq-md ai-sq-md--sm" : "ai-sq-md"}
          style={style}
          dangerouslySetInnerHTML={{
            __html: html || "<p style='color:#94a3b8'>No content available.</p>",
          }}
        />
      </MathJax>
    );
  };

  nextQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.min(prev.currentIndex + 1, prev.similar.length - 1),
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    }));
  };

  prevQuestion = () => {
    this.setState((prev) => ({
      currentIndex: Math.max(prev.currentIndex - 1, 0),
      showExplanation: false,
      showDetailedExplanation: false,
      selectedOption: null,
      answered: false,
    }));
  };

  // Build an MCQ text block from a question object for the explain API
  buildMcqText = (qObj) => {
    if (!qObj) return "";
    const parts = [];
    parts.push(`Q. ${qObj.question}`);
    const opts = qObj.options || {};
    for (const k of ["A", "B", "C", "D"]) {
      if (opts[k]) parts.push(`${k}. ${opts[k]}`);
    }
    if (qObj.correct_answer) parts.push(`Correct Answer: ${qObj.correct_answer}`);
    return parts.join(" \n");
  };

  fetchExplanationForCurrent = async () => {
    const { similar, currentIndex } = this.state;
    const current = similar[currentIndex];
    if (!current) return;

    // If we already have an explanation, just toggle show
    if (current.explanation && current.explanation.trim() !== "") {
      this.setState({ showExplanation: true });
      return;
    }

    const mcqText = this.buildMcqText(current);
    const url = `https://ai.rizee.in/explain-mcq-3?mcq=${this.encodeForm(mcqText)}&student_class=&subject=`;

    // open explanation area immediately and show its loading spinner
    // Keep detailed explanation hidden until the user requests it via button.
    this.setState({ explanationLoading: true, error: null, showExplanation: true, showDetailedExplanation: false });
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Explain API error ${res.status}: ${t}`);
      }

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = raw;
      }

      // accept keys 'exaplation' (typo) or 'explanation'
      let explanation = null;
      if (data && typeof data === "object") {
        explanation = data.exaplation || data.explanation || data.explanations || null;
        if (Array.isArray(explanation)) explanation = explanation.join("\n\n");
      } else if (typeof data === "string") {
        explanation = data;
      }

      explanation = this.normalizeMathText(explanation || "No explanation provided.");

      // update similar[currentIndex].explanation immutably and persist to cache
      this.setState((prev) => {
        const newSimilar = prev.similar.map((s, idx) => {
          if (idx !== prev.currentIndex) return s;
          return { ...s, explanation };
        });
        // Persist updated explanations to cache when a question_id is present
        if (this.props.question_id) {
          try {
            this.saveToCache({ storedAt: Date.now(), question_id: this.props.question_id, questions: newSimilar });
          } catch (e) {
            // ignore cache failures
          }
        }

        // after short explanation is received, keep detailed hidden until requested
        return { similar: newSimilar, explanationLoading: false, showExplanation: true, showDetailedExplanation: false };
      });
    } catch (err) {
      this.setState({ error: err.message || String(err), explanationLoading: false });
    }
  };

  // Fetch detailed explanation from a separate API (configurable via prop)
  fetchDetailedExplanationForCurrent = async () => {
    const { similar, currentIndex } = this.state;
    const current = similar[currentIndex];
    if (!current) return;

    // If we already have a detailed explanation, just show it and hide short explanation
    if (current.detailed_explanation && current.detailed_explanation.trim() !== "") {
      this.setState({ showDetailedExplanation: true, showExplanation: false });
      return;
    }

    const mcqText = this.buildMcqText(current);
    // endpoint can be passed as prop `detailedExplainUrl`, otherwise default
    const endpoint = this.props.detailedExplainUrl || "https://ai.rizee.in/explain-mcq-detailed-3";
    const url = `${endpoint}?mcq=${this.encodeForm(mcqText)}&student_class=&subject=`;

    // open detailed area immediately and show its loading spinner
    // Hide the short explanation when displaying detailed content (mutually exclusive)
    this.setState({ detailedLoading: true, error: null, showDetailedExplanation: true, showExplanation: false });
    try {
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Detailed Explain API error ${res.status}: ${t}`);
      }

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        data = raw;
      }

      let detailed = null;
      if (data && typeof data === "object") {
        detailed = data.exaplation || data.explanation || data.detailed_explanation || data.explanations || null;
        if (Array.isArray(detailed)) detailed = detailed.join("\n\n");
      } else if (typeof data === "string") {
        detailed = data;
      }

      detailed = this.normalizeMathText(detailed || "No detailed explanation provided.");

      // update similar[currentIndex].detailed_explanation immutably and persist to cache
      this.setState((prev) => {
        const newSimilar = prev.similar.map((s, idx) => {
          if (idx !== prev.currentIndex) return s;
          return { ...s, detailed_explanation: detailed };
        });

        if (this.props.question_id) {
          try {
            this.saveToCache({ storedAt: Date.now(), question_id: this.props.question_id, questions: newSimilar });
          } catch (e) {
            // ignore
          }
        }

        return { similar: newSimilar, detailedLoading: false, showDetailedExplanation: true, showExplanation: false };
      });
    } catch (err) {
      this.setState({ error: err.message || String(err), detailedLoading: false });
    }
  };

  render() {


    const { modalShow, similar, loading, error, currentIndex, showExplanation, showDetailedExplanation, answered, activeTab, detailsRequested } = this.state;
    const current = similar[currentIndex] || null;


    console.log("myQue",this.props.question);
    

    const config = {
      loader: { load: ["[tex]/html"] },
      tex: {
        inlineMath: [["$", "$"], ["\\(", "\\)"]],
        displayMath: [["$$", "$$"], ["\\[", "\\]"]],
      },
      svg: { fontCache: "global" },
    };

    const total = similar.length || 0;
    const progressPct = total ? ((currentIndex + 1) / total) * 100 : 0;

    return (
      <MathJaxContext config={config}>
        <style>
          {`
            /* ============ Shell ============ */
            /* dialogClassName puts this class ON .modal-dialog itself */
            .ai-sq-modal.modal-dialog { margin: 1.25rem auto; }

            .ai-sq-modal .modal-content {
              border: none;
              border-radius: 20px;
              overflow: hidden;
              background: #ffffff;
              box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28), 0 2px 8px rgba(15, 23, 42, 0.08);
              animation: aiSqPop 0.32s cubic-bezier(0.22, 1, 0.36, 1);
            }

            @keyframes aiSqPop {
              from { opacity: 0; transform: translateY(14px) scale(0.985); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }

            @keyframes aiSqRise {
              from { opacity: 0; transform: translateY(10px); }
              to   { opacity: 1; transform: translateY(0); }
            }

            @keyframes aiSqShimmer {
              0%   { background-position: -420px 0; }
              100% { background-position: 420px 0; }
            }

            @keyframes aiSqOrbit {
              to { transform: rotate(360deg); }
            }

            /* ============ Header ============ */
            .ai-sq-header {
              position: relative;
              padding: 14px 18px !important;
              border: none !important;
              background: linear-gradient(120deg, #4f46e5 0%, #6d28d9 52%, #7c3aed 100%);
              align-items: center !important;
            }

            .ai-sq-header::after {
              content: "";
              position: absolute;
              inset: 0;
              background: radial-gradient(120% 180% at 100% 0%, rgba(255,255,255,0.22), transparent 60%);
              pointer-events: none;
            }

            .ai-sq-header .modal-title { width: 100%; }

            /* Bootstrap 4 (.close) + Bootstrap 5 (.btn-close) close button */
            .ai-sq-header .close {
              color: #fff !important;
              opacity: 0.85;
              text-shadow: none;
              font-size: 26px;
              font-weight: 400;
              transition: opacity 0.2s ease, transform 0.2s ease;
              position: relative;
              z-index: 2;
            }
            .ai-sq-header .close:hover { opacity: 1; transform: rotate(90deg); }

            .ai-sq-header .btn-close {
              filter: brightness(0) invert(1);
              opacity: 0.85;
              transition: opacity 0.2s ease, transform 0.2s ease;
              position: relative;
              z-index: 2;
            }
            .ai-sq-header .btn-close:hover { opacity: 1; transform: rotate(90deg); }

            .ai-sq-title-wrap {
              display: flex;
              align-items: center;
              gap: 12px;
              position: relative;
              z-index: 2;
            }

            .ai-sq-title-icon {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 38px;
              height: 38px;
              flex: 0 0 38px;
              border-radius: 12px;
              font-size: 18px;
              background: rgba(255, 255, 255, 0.18);
              border: 1px solid rgba(255, 255, 255, 0.28);
              backdrop-filter: blur(6px);
            }

            .ai-sq-title-text {
              font-size: 16px;
              font-weight: 800;
              letter-spacing: 0.2px;
              color: #ffffff;
              line-height: 1.2;
            }

            .ai-sq-title-sub {
              display: block;
              font-size: 11.5px;
              font-weight: 500;
              letter-spacing: 0.3px;
              color: rgba(255, 255, 255, 0.78);
              margin-top: 2px;
            }

            /* ============ Tabs ============ */
            .ai-sq-tabs {
              display: flex;
              gap: 6px;
              padding: 6px;
              margin-bottom: 14px;
              border-radius: 14px;
              background: rgba(99, 102, 241, 0.08);
              border: 1px solid rgba(99, 102, 241, 0.14);
            }

            .ai-sq-tab {
              flex: 1;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding: 10px 14px;
              border: none;
              border-radius: 10px;
              background: transparent;
              color: #64748b;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0.2px;
              cursor: pointer;
              white-space: nowrap;
              transition: color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
            }

            .ai-sq-tab:hover:not(.ai-sq-tab--active) {
              color: #4338ca;
              background: rgba(255, 255, 255, 0.6);
            }

            .ai-sq-tab:focus-visible { outline: 2px solid #4f46e5; outline-offset: -2px; }

            .ai-sq-tab--active {
              background: #ffffff;
              color: #4338ca;
              box-shadow: 0 4px 12px rgba(15, 23, 42, 0.10);
            }

            .ai-sq-tab--details.ai-sq-tab--active { color: #0f766e; }
            .ai-sq-tab--details:hover:not(.ai-sq-tab--active) { color: #0f766e; }

            .ai-sq-tabpanel { animation: aiSqRise 0.3s ease both; }

            /* ============ Body ============ */
            .ai-sq-body {
              background: linear-gradient(180deg, #f8fafc 0%, #f1f3fb 100%);
              padding: 18px !important;
              max-height: 74vh;
              overflow-y: auto;
            }

            .ai-sq-body::-webkit-scrollbar { width: 8px; }
            .ai-sq-body::-webkit-scrollbar-thumb {
              background: rgba(99, 102, 241, 0.28);
              border-radius: 999px;
            }
            .ai-sq-body::-webkit-scrollbar-thumb:hover { background: rgba(99, 102, 241, 0.45); }

            .ai-sq-card {
              background: #ffffff;
              border: 1px solid rgba(99, 102, 241, 0.14);
              border-radius: 18px;
              box-shadow: 0 8px 26px rgba(15, 23, 42, 0.07);
              animation: aiSqRise 0.35s ease both;
            }

            /* ============ Progress ============ */
            .ai-sq-progress-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              flex-wrap: wrap;
              margin-bottom: 10px;
            }

            .ai-sq-chip {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 5px 12px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.3px;
              color: #4338ca;
              background: rgba(99, 102, 241, 0.10);
              border: 1px solid rgba(99, 102, 241, 0.22);
            }

            .ai-sq-chip--muted {
              color: #64748b;
              background: rgba(100, 116, 139, 0.08);
              border-color: rgba(100, 116, 139, 0.18);
            }

            .ai-sq-progress-track {
              height: 6px;
              border-radius: 999px;
              background: rgba(99, 102, 241, 0.12);
              overflow: hidden;
            }

            .ai-sq-progress-fill {
              height: 100%;
              border-radius: 999px;
              background: linear-gradient(90deg, #6366f1, #a855f7);
              transition: width 0.45s cubic-bezier(0.22, 1, 0.36, 1);
            }

            /* ============ Question ============ */
            .ai-sq-question {
              position: relative;
              padding: 16px 16px 16px 20px;
              margin-bottom: 14px;
              overflow: hidden;
            }

            .ai-sq-question::before {
              content: "";
              position: absolute;
              left: 0; top: 0; bottom: 0;
              width: 4px;
              background: linear-gradient(180deg, #6366f1, #a855f7);
            }

            /* ============ Options ============ */
            .ai-options-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px;
            }

            .ai-sq-option {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding: 11px 13px;
              border-radius: 14px;
              border: 1.5px solid #e6e8f0;
              background: #fbfcfe;
              transition: transform 0.18s ease, box-shadow 0.18s ease,
                          border-color 0.18s ease, background 0.18s ease;
              animation: aiSqRise 0.3s ease both;
            }

            .ai-sq-option--idle { cursor: pointer; }

            .ai-sq-option--idle:hover {
              transform: translateY(-2px);
              border-color: rgba(99, 102, 241, 0.55);
              background: #ffffff;
              box-shadow: 0 10px 22px rgba(79, 70, 229, 0.14);
            }

            .ai-sq-option--idle:active { transform: translateY(0); }

            .ai-sq-option--correct {
              border-color: rgba(16, 185, 129, 0.55);
              background: linear-gradient(180deg, rgba(16,185,129,0.10), rgba(16,185,129,0.05));
              box-shadow: 0 8px 20px rgba(16, 185, 129, 0.16);
            }

            .ai-sq-option--wrong {
              border-color: rgba(239, 68, 68, 0.5);
              background: linear-gradient(180deg, rgba(239,68,68,0.09), rgba(239,68,68,0.04));
              box-shadow: 0 8px 20px rgba(239, 68, 68, 0.14);
            }

            .ai-sq-option--dim { opacity: 0.62; }

            .ai-sq-key {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 26px;
              height: 26px;
              flex: 0 0 26px;
              border-radius: 9px;
              font-size: 12px;
              font-weight: 800;
              color: #4338ca;
              background: rgba(99, 102, 241, 0.12);
              border: 1px solid rgba(99, 102, 241, 0.20);
              transition: all 0.18s ease;
            }

            .ai-sq-option--correct .ai-sq-key {
              color: #ffffff;
              background: #10b981;
              border-color: #10b981;
            }

            .ai-sq-option--wrong .ai-sq-key {
              color: #ffffff;
              background: #ef4444;
              border-color: #ef4444;
            }

            .ai-sq-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 22px;
              height: 22px;
              flex: 0 0 22px;
              border-radius: 50%;
              font-size: 11px;
              font-weight: 800;
              color: #ffffff;
              animation: aiSqPop 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
            }

            .ai-sq-badge--ok { background: #10b981; }
            .ai-sq-badge--no { background: #ef4444; }

            /* ============ Buttons ============ */
            .ai-sq-btn {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 7px;
              padding: 9px 18px;
              border-radius: 999px;
              border: none;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0.2px;
              color: #ffffff;
              cursor: pointer;
              transition: transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease;
              white-space: nowrap;
            }

            .ai-sq-btn:hover { transform: translateY(-2px); filter: brightness(1.05); }
            .ai-sq-btn:active { transform: translateY(0); }
            .ai-sq-btn:focus-visible { outline: 2px solid #4f46e5; outline-offset: 2px; }

            .ai-sq-btn:disabled {
              opacity: 0.55;
              cursor: not-allowed;
              transform: none;
              box-shadow: none;
              filter: none;
            }

            .ai-sq-btn--indigo {
              background: linear-gradient(135deg, #6366f1, #8b5cf6);
              box-shadow: 0 8px 18px rgba(99, 102, 241, 0.32);
            }
            .ai-sq-btn--indigo:hover { box-shadow: 0 12px 24px rgba(99, 102, 241, 0.42); }

            .ai-sq-btn--emerald {
              background: linear-gradient(135deg, #10b981, #059669);
              box-shadow: 0 8px 18px rgba(16, 185, 129, 0.30);
            }
            .ai-sq-btn--emerald:hover { box-shadow: 0 12px 24px rgba(16, 185, 129, 0.40); }

            .ai-sq-btn--rose {
              background: linear-gradient(135deg, #f43f5e, #e11d48);
              box-shadow: 0 8px 18px rgba(244, 63, 94, 0.30);
            }
            .ai-sq-btn--rose:hover { box-shadow: 0 12px 24px rgba(244, 63, 94, 0.40); }

            .ai-sq-btn--slate {
              background: linear-gradient(135deg, #64748b, #475569);
              box-shadow: 0 8px 18px rgba(71, 85, 105, 0.26);
            }
            .ai-sq-btn--slate:hover { box-shadow: 0 12px 24px rgba(71, 85, 105, 0.34); }

            .ai-sq-nav {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              min-width: 118px;
              padding: 10px 18px;
              border-radius: 12px;
              border: 1.5px solid #e2e8f0;
              background: #ffffff;
              color: #334155;
              font-size: 13px;
              font-weight: 700;
              cursor: pointer;
              transition: transform 0.18s ease, box-shadow 0.18s ease,
                          border-color 0.18s ease, color 0.18s ease;
            }

            .ai-sq-nav:hover:not(:disabled) {
              transform: translateY(-2px);
              color: #4338ca;
              border-color: rgba(99, 102, 241, 0.5);
              box-shadow: 0 10px 20px rgba(15, 23, 42, 0.10);
            }

            .ai-sq-nav:active:not(:disabled) { transform: translateY(0); }

            .ai-sq-nav:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              background: #f1f5f9;
              color: #94a3b8;
              box-shadow: none;
            }

            /* ============ Loading / states ============ */
            .ai-sq-loader {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 14px;
              padding: 34px 18px;
            }

            .ai-sq-spinner {
              width: 46px;
              height: 46px;
              border-radius: 50%;
              border: 3px solid rgba(99, 102, 241, 0.18);
              border-top-color: #6366f1;
              border-right-color: #a855f7;
              animation: aiSqOrbit 0.85s linear infinite;
            }

            .ai-sq-loader-text {
              font-size: 13.5px;
              font-weight: 700;
              color: #4338ca;
            }

            .ai-sq-loader-sub { font-size: 12px; color: #94a3b8; }

            .ai-sq-skeleton {
              width: 100%;
              max-width: 460px;
              height: 12px;
              border-radius: 999px;
              background: linear-gradient(90deg, #eef1f8 25%, #e2e8f0 37%, #eef1f8 63%);
              background-size: 840px 100%;
              animation: aiSqShimmer 1.3s linear infinite;
            }

            .ai-sq-state {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              padding: 16px 18px;
              border-radius: 16px;
              font-size: 13px;
              font-weight: 600;
              animation: aiSqRise 0.3s ease both;
            }

            .ai-sq-state--error {
              color: #9f1239;
              background: linear-gradient(180deg, rgba(244,63,94,0.10), rgba(244,63,94,0.05));
              border: 1px solid rgba(244, 63, 94, 0.28);
            }

            .ai-sq-state--empty {
              color: #92400e;
              background: linear-gradient(180deg, rgba(245,158,11,0.12), rgba(245,158,11,0.06));
              border: 1px solid rgba(245, 158, 11, 0.30);
            }

            .ai-sq-state-icon { font-size: 18px; line-height: 1.2; }

            .ai-sq-inline-loader {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 8px 2px;
              font-size: 12px;
              font-weight: 600;
              color: inherit;
              opacity: 0.85;
            }

            /* ============ Explanation panels ============ */
            .ai-sq-explain {
              position: relative;
              padding: 14px 16px 14px 18px;
              border-radius: 16px;
              overflow: hidden;
              animation: aiSqRise 0.32s ease both;
            }

            .ai-sq-explain::before {
              content: "";
              position: absolute;
              left: 0; top: 0; bottom: 0;
              width: 4px;
              background: var(--ai-sq-accent);
            }

            .ai-sq-explain--amber {
              --ai-sq-accent: linear-gradient(180deg, #f59e0b, #d97706);
              --ai-sq-fg: #78350f;
              background: linear-gradient(180deg, #fffbeb, #fef6e0);
              border: 1px solid rgba(245, 158, 11, 0.28);
            }

            .ai-sq-explain--blue {
              --ai-sq-accent: linear-gradient(180deg, #3b82f6, #6366f1);
              --ai-sq-fg: #1e3a8a;
              background: linear-gradient(180deg, #eff6ff, #eaf0fe);
              border: 1px solid rgba(59, 130, 246, 0.26);
            }

            .ai-sq-explain-head {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 12px;
              font-weight: 800;
              letter-spacing: 0.4px;
              text-transform: uppercase;
              color: var(--ai-sq-fg);
              margin-bottom: 8px;
              opacity: 0.9;
            }

            /* ============ Markdown / MathJax typography ============ */
            /* No horizontal scrollbars anywhere: long text and long formulas
               wrap onto the next line so the whole question stays visible. */
            .ai-sq-md {
              overflow: visible;
              overflow-wrap: anywhere;
              word-break: break-word;
            }
            .ai-sq-md > *:first-child { margin-top: 0 !important; }
            .ai-sq-md > *:last-child { margin-bottom: 0 !important; }
            .ai-sq-md img { max-width: 100%; height: auto; }
            .ai-sq-md table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            .ai-sq-md th, .ai-sq-md td {
              border: 1px solid rgba(15, 23, 42, 0.10);
              padding: 6px 8px;
              overflow-wrap: anywhere;
            }
            .ai-sq-md code {
              background: rgba(15, 23, 42, 0.06);
              padding: 1px 5px;
              border-radius: 5px;
            }
            .ai-sq-md pre {
              white-space: pre-wrap;
              word-break: break-word;
              margin: 6px 0;
            }

            /* MathJax defaults to nowrap + its own scroll container; unset both
               so long expressions wrap instead of producing a scrollbar. */
            .ai-sq-md mjx-container,
            .ai-explanation mjx-container {
              max-width: 100% !important;
              overflow: visible !important;
              white-space: normal !important;
              display: inline-block;
            }
            .ai-sq-md mjx-container mjx-math,
            .ai-explanation mjx-container mjx-math {
              max-width: 100% !important;
              white-space: normal !important;
              overflow-wrap: anywhere;
            }
            .ai-sq-md mjx-container[display="true"],
            .ai-explanation mjx-container[display="true"] { display: block; }
            .ai-sq-md .MathJax, .ai-sq-md .mjx-chtml,
            .ai-explanation .MathJax, .ai-explanation .mjx-chtml {
              max-width: 100% !important;
              white-space: normal !important;
              overflow: visible !important;
            }

            /* Explanation area: force smaller font and scale MathJax output
               We apply multiple fallbacks (inherit, direct selectors and transform)
               to override MathJax's internal sizing and inline styles. */
            .ai-explanation { font-size: 12px !important; line-height: 1.55 !important; }
            .ai-explanation, .ai-explanation * { font-size: 12px !important; line-height: 1.55 !important; }

            /* Reduce sizes for markdown elements coming from the AI response */
            .ai-explanation h1, .ai-explanation h2, .ai-explanation h3, .ai-explanation h4, .ai-explanation h5, .ai-explanation h6 {
              font-size: 13.5px !important;
              margin-top: 10px !important;
              margin-bottom: 6px !important;
              line-height: 1.3 !important;
              font-weight: 800 !important;
              color: var(--ai-sq-fg) !important;
            }

            .ai-explanation p, .ai-explanation li {
              font-size: 12px !important;
              margin: 5px 0 !important;
              color: var(--ai-sq-fg) !important;
            }

            .ai-explanation strong { color: var(--ai-sq-fg) !important; font-weight: 800 !important; }

            .ai-explanation ul, .ai-explanation ol { padding-left: 18px !important; margin: 6px 0 !important; }
            .ai-explanation hr { border: 0 !important; border-top: 1px solid rgba(15,23,42,0.10) !important; margin: 12px 0 !important; }

            /* Target MathJax renderers and force inheritence/scale */
            .ai-explanation .mjx-chtml, .ai-explanation .mjx-svg, .ai-explanation .MathJax { font-size: 12px !important; }
            .ai-explanation .mjx-chtml, .ai-explanation .mjx-svg { transform: scale(0.82); transform-origin: 0 0; }
            .ai-explanation .mjx-chtml * { font-size: inherit !important; }
            .ai-explanation .mjx-svg svg { width: auto !important; height: auto !important; }
            .ai-explanation svg { max-width: 100% !important; height: auto !important; }

            /* ============ Responsive ============ */
            @media (max-width: 767.98px) {
              .ai-sq-modal.modal-dialog { margin: 0.5rem; }
              .ai-sq-modal .modal-content { border-radius: 16px; }
              .ai-sq-body { padding: 14px !important; max-height: 80vh; }
              .ai-sq-title-text { font-size: 15px; }
            }

            @media (max-width: 575.98px) {
              .ai-options-grid { grid-template-columns: 1fr !important; }
              .ai-sq-header { padding: 12px 14px !important; }
              .ai-sq-title-icon { width: 32px; height: 32px; flex-basis: 32px; font-size: 15px; }
              .ai-sq-title-sub { display: none; }
              .ai-sq-actions { flex-direction: column; align-items: stretch; }
              .ai-sq-actions .ai-sq-btn { width: 100%; }
              .ai-sq-tab { padding: 9px 8px; font-size: 12px; gap: 5px; }
              .ai-sq-nav { min-width: 0; flex: 1; padding: 10px 12px; }
              .ai-sq-question { padding: 13px 13px 13px 16px; }
            }

            @media (prefers-reduced-motion: reduce) {
              .ai-sq-modal .modal-content,
              .ai-sq-card, .ai-sq-option, .ai-sq-badge, .ai-sq-explain, .ai-sq-skeleton {
                animation: none !important;
              }
              .ai-sq-btn, .ai-sq-nav, .ai-sq-option { transition: none !important; }
            }
          `}
        </style>

        <Modal
          show={modalShow}
          onHide={this.close}
          size="lg"
          // centered
          dialogClassName="ai-sq-modal"
        >
          <Modal.Header closeButton className="ai-sq-header">
            <Modal.Title as="div">
              <div className="ai-sq-title-wrap">
                <span className="ai-sq-title-icon">📝</span>
                <span>
                  <span className="ai-sq-title-text">Similar Questions</span>
                  <span className="ai-sq-title-sub">Practice MCQs generated for this concept</span>
                </span>
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="ai-sq-body">
            {/* ✅ Tabs: Similar Questions (default) | Details Explanation */}
            {/* <div className="ai-sq-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "similar"}
                className={`ai-sq-tab${activeTab === "similar" ? " ai-sq-tab--active" : ""}`}
                onClick={() => this.selectTab("similar")}
              >
                <span>📝</span>Similar Questions
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "details"}
                className={`ai-sq-tab ai-sq-tab--details${activeTab === "details" ? " ai-sq-tab--active" : ""}`}
                onClick={() => this.selectTab("details")}
              >
                <span>📖</span>Details Explanation
              </button>
            </div> */}

            {/* Kept mounted so answers//explanations survive a tab switch. */}
            <div
              role="tabpanel"
              className="ai-sq-tabpanel"
              style={{ display: activeTab === "similar" ? "block" : "none" }}
            >
            {loading && (!similar || similar.length === 0) && (
              <div className="ai-sq-card ai-sq-loader">
                <div className="ai-sq-spinner" />
                <div className="ai-sq-loader-text">Generating similar questions…</div>
                <div className="ai-sq-loader-sub">This usually takes a few seconds</div>
                <div className="ai-sq-skeleton" />
                <div className="ai-sq-skeleton" style={{ maxWidth: 360 }} />
                <div className="ai-sq-skeleton" style={{ maxWidth: 260 }} />
              </div>
            )}

            {!loading && error && (
              <div className="ai-sq-state ai-sq-state--error">
                <span className="ai-sq-state-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && similar.length === 0 && (
              <div className="ai-sq-state ai-sq-state--empty">
                <span className="ai-sq-state-icon">🔍</span>
                <span>No similar questions found.</span>
              </div>
            )}

            {!loading && !error && current && (
              <>
                {/* ✅ Top Question (MathJax display) */}
                <div style={styles.topBar}>
                  <div className="ai-sq-progress-head">
                    <span className="ai-sq-chip">
                      Question {currentIndex + 1} of {total}
                    </span>
                    <span className="ai-sq-chip ai-sq-chip--muted">
                      {answered ? "Answered" : "Pick an answer"}
                    </span>
                  </div>

                  <div className="ai-sq-progress-track">
                    <div className="ai-sq-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>

                  <div className="ai-sq-card ai-sq-question">
                    <div style={styles.topQuestionBox}>
                      {this.renderMathContent(current.question)}
                    </div>
                  </div>
                </div>

                <div className="ai-sq-card" style={styles.outputCard}>
                  {/* ✅ Options */}
                  <div style={styles.answerBox}>
                    <div style={styles.label}>
                      <span style={styles.labelDot} />
                      Choose the correct option
                    </div>

                    <div className="ai-options-grid">
                      {Object.entries(current.options || {}).map(([key, value], idx) => {
                        const { selectedOption, answered } = this.state;
                        const correctKey = current.correct_answer || null;
                        const isCorrect = correctKey && key === correctKey;
                        const isSelected = selectedOption === key;

                        // Compute dynamic classes
                        let cardClass = "ai-sq-option";
                        if (answered) {
                          if (isCorrect) cardClass += " ai-sq-option--correct";
                          else if (isSelected && !isCorrect) cardClass += " ai-sq-option--wrong";
                          else cardClass += " ai-sq-option--dim";
                        } else {
                          cardClass += " ai-sq-option--idle";
                        }

                        return (
                          <div
                            key={key}
                            className={cardClass}
                            style={{
                              cursor: answered ? "default" : "pointer",
                              animationDelay: `${idx * 45}ms`,
                            }}
                            onClick={() => this.handleSelectOption(key)}
                          >
                            <div style={styles.optionInner}>
                              <span className="ai-sq-key">{key}</span>
                              <div style={styles.optionValue}>{this.renderMathContent(value)}</div>
                            </div>
                            {answered && isCorrect && <span className="ai-sq-badge ai-sq-badge--ok">✔</span>}
                            {answered && isSelected && !isCorrect && (
                              <span className="ai-sq-badge ai-sq-badge--no">✖</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="ai-sq-actions" style={styles.actionsRow}>
                    {this.state.answered ? (
                      <>
                        <button
                          type="button"
                          className={`ai-sq-btn ${showExplanation ? "ai-sq-btn--rose" : "ai-sq-btn--indigo"}`}
                          onClick={() => {
                            if (showExplanation) {
                              this.setState({ showExplanation: false });
                            } else {
                              // hide detailed immediately, then fetch short explanation
                              this.setState({ showDetailedExplanation: false }, () => this.fetchExplanationForCurrent());
                            }
                          }}
                        >
                          {showExplanation ? (
                            <>
                              <span>🧠</span>Hide Explanation
                            </>
                          ) : (
                            <>
                              <span>🧠</span>Explain
                            </>
                          )}
                        </button>

                        {/* Detailed Explain button - visible once user answered (always shown alongside Explain) */}
                        <button
                          type="button"
                          className={`ai-sq-btn ${this.state.showDetailedExplanation ? "ai-sq-btn--slate" : "ai-sq-btn--emerald"}`}
                          onClick={() => {
                            if (this.state.showDetailedExplanation) {
                              this.setState({ showDetailedExplanation: false });
                            } else {
                              // hide short explanation immediately, then fetch detailed
                              this.setState({ showExplanation: false }, () => this.fetchDetailedExplanationForCurrent());
                            }
                          }}
                        >
                          {this.state.showDetailedExplanation ? (
                            <>
                              <span>📘</span>Hide Detailed
                            </>
                          ) : (
                            <>
                              <span>📘</span>Detailed Explain
                            </>
                          )}
                        </button>
                      </>
                    ) : null}
                  </div>

                  {/* ✅ Explanation */}
                  {showExplanation && (
                    <div className="ai-sq-explain ai-sq-explain--amber ai-explanation">
                      <div className="ai-sq-explain-head">
                        <span>📌</span>Explanation
                      </div>
                      <div style={styles.explanationText}>
                        {this.state.explanationLoading ? (
                          <div className="ai-sq-inline-loader">
                            <Spinner animation="border" size="sm" />
                            <span>Preparing explanation…</span>
                          </div>
                        ) : (
                          this.renderMathContent(current.explanation, 'small')
                        )}
                      </div>
                    </div>
                  )}

                  {/* Detailed Explanation */}
                  {this.state.showDetailedExplanation && (
                    <div className="ai-sq-explain ai-sq-explain--blue ai-explanation">
                      <div className="ai-sq-explain-head">
                        <span>🔎</span>Detailed Explanation
                      </div>
                      <div style={styles.explanationText}>
                        {this.state.detailedLoading ? (
                          <div className="ai-sq-inline-loader">
                            <Spinner animation="border" size="sm" />
                            <span>Building a step-by-step walkthrough…</span>
                          </div>
                        ) : (
                          this.renderMathContent((current && current.detailed_explanation) || "", 'small')
                        )}
                      </div>
                    </div>
                  )}

                  {/* ✅ Navigation */}
                  <div style={styles.navRow}>
                    <button
                      type="button"
                      className="ai-sq-nav"
                      onClick={this.prevQuestion}
                      disabled={currentIndex === 0}
                    >
                      <span>⬅</span>Prev
                    </button>

                    <button
                      type="button"
                      className="ai-sq-nav"
                      onClick={this.nextQuestion}
                      disabled={currentIndex === similar.length - 1}
                    >
                      Next<span>➡</span>
                    </button>
                  </div>

                  {/* ✅ Generate - asks the API for 3 more than last time and
                      replaces the set with what comes back. */}
                  <div style={styles.generateRow}>
                    <button
                      type="button"
                      className="ai-sq-btn ai-sq-btn--indigo"
                      onClick={this.generateMore}
                      disabled={loading}
                    >
                      <span>✨</span>Generate 3 More
                    </button>
                  </div>
                </div>
              </>
            )}
            </div>

            {/* Details Explanation tab - mounted (and its API called) only
                once the user has opened this tab at least once. */}
            {detailsRequested && (
              <div
                role="tabpanel"
                style={{ display: activeTab === "details" ? "block" : "none" }}
              >
                <DetailsExplanation
                  question={this.props.question}
                  question_id={this.props.question_id}
                  apiBase={this.props.detailsApiBase}
                />
              </div>
            )}
          </Modal.Body>
        </Modal>
      </MathJaxContext>
    );
  }
}

/* ✅ FINAL UI STYLES
   Layout/spacing lives here; colours, hover, focus and motion live in the
   scoped <style> block above (inline styles can't express pseudo-states). */
const styles = {
  topBar: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "14px",
  },

  topQuestionBox: {
    fontWeight: 600,
    color: "#0f172a",
    fontSize: "14.5px",
    lineHeight: "1.6",
  },

  outputCard: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  answerBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 800,
    fontSize: "12px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: "#64748b",
  },

  labelDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "linear-gradient(135deg,#6366f1,#a855f7)",
    display: "inline-block",
  },

  optionInner: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: 0,
  },

  optionValue: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: "13.5px",
    lineHeight: "1.45",
    color: "#1e293b",
  },

  actionsRow: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "10px",
  },

  explanationText: {
    color: "var(--ai-sq-fg)",
    fontSize: "12px",
    lineHeight: "1.55",
  },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    paddingTop: "4px",
    borderTop: "1px solid rgba(15,23,42,0.06)",
    marginTop: "2px",
  },

  generateRow: {
    display: "flex",
    justifyContent: "center",
  },

  answerContent: {
    lineHeight: "1.65",
    fontSize: "14px",
  },
};

export default AiSimilarQuestionModels2;
